/**
 * Reusable result cards rendered inline in the assistant chat bubble.
 *
 * When a tool matches structured results (e.g. specific emails), the agent
 * emits per-result `result.card` uiActions. useChat partitions those out and
 * appends `RESULT_CARDS_MARKER + JSON(ResultCard[])` to the FINAL assistant
 * message content (so the cards survive persistence + reload). ChatBox detects
 * the marker via {@link splitResultCards} and renders <ResultCardRenderer>
 * after the plain markdown body.
 */

export const RESULT_CARDS_MARKER = '@@XAVIOR_RESULT_CARDS@@';

// 'today' is the pull "what's on my plate" card; 'briefing' is its legacy kind
// (older persisted messages carry it) — both render the same card.
export type ResultCardKind = 'email' | 'note' | 'doc' | 'contact' | 'place' | 'task' | 'email_draft' | 'events' | 'inbox' | 'inbox_triage' | 'today' | 'briefing' | 'report' | 'travel' | 'reservations' | 'circle' | 'persona' | 'social';

/** One curated row on the inbox-triage card. */
export interface InboxTriageRow {
  kind: 'person' | 'delivery' | 'security' | 'finance' | 'event' | 'notice';
  senderName: string;
  senderEmail: string;
  subject: string;
  snippet: string;
  date: string;
  isUnread: boolean;
  threadId: string;
  messageId: string;
  integrationId: string;
  senderPhotoUrl?: string | null;
}

/** Payload of the `inbox_triage` card — people needing replies + notable
 *  notifications, with the filtered noise counted, not shown. */
export interface InboxTriageCardData {
  people: InboxTriageRow[];
  notifications: InboxTriageRow[];
  hiddenCount: number;
  duplicates: number;
}

/** One event inside an {@link EventsCardData}. Times are ISO 8601. */
export interface EventsCardEvent {
  /** Unique row id (for recurring events this is `${masterId}:${occurrenceISO}`,
   *  so it stays unique per occurrence for React keys). */
  id: string;
  /** Real workspace OBJECT id used for click-through. Differs from `id` for a
   *  recurring occurrence (it's the master event's id); falls back to `id`. */
  objectId?: string;
  title: string;
  start: string;
  end?: string;
  allDay?: boolean;
  location?: string;
  attendees?: Array<{ name?: string; email: string; photoUrl?: string | null }>;
}

/** ONE card holding a schedule for a date range — a glanceable week strip plus a
 *  day-grouped agenda. The agent emits this for "what's on my schedule" queries. */
export interface EventsCardData {
  /** Human label for the range, e.g. "Next week" or "Jun 2 – 8". */
  rangeLabel: string;
  /** ISO 8601 bounds of the queried window (drive the week strip + Open Calendar). */
  rangeStart: string;
  rangeEnd: string;
  events: EventsCardEvent[];
}

export interface EmailCardData {
  messageId: string;
  threadId: string;
  fromName: string;
  fromEmail: string;
  subject: string;
  snippet: string;
  /** ISO 8601 timestamp. */
  date: string;
  isUnread: boolean;
  /** 1-based index of this match within the result set. */
  matchedIndex?: number;
  matchedOf?: number;
  senderPhotoUrl?: string | null;
}

/** Editable email draft the agent proposes; the user can tweak + send inline. */
export interface EmailDraftCardData {
  to: string;
  toName?: string;
  subject: string;
  /** May be empty (compose-pending) or hold the user's rough note to compose from. */
  body: string;
  /** Workspace contact this draft is addressed to; required to compose voices. */
  contactId?: string;
  /** Outreach type sent to the draft endpoint when composing. Defaults to 'casual'. */
  actionType?: string;
  /** True when the card is awaiting a voice choice (To filled, body not yet generated). */
  composePending?: boolean;
  /** Reply context (set when replying to an inbox email). When `threadId` is
   *  present the card is in REPLY mode: "Xavior write" drafts a contextual reply
   *  via /draft-reply (no contactId needed) and Send delivers in-thread. */
  threadId?: string;
  messageId?: string;
  replyMode?: boolean;
  /** Account that owns the thread — sent as accountId so the reply threads onto
   *  and sends from the correct mailbox (matters for multi-account inboxes). */
  accountId?: string;
}

/** ONE card holding the priority inbox — emails the user needs to deal with,
 *  rendered as a clickable grid of chips. Clicking a chip opens a reply
 *  composer with a "Xavior write" option. */
export interface InboxCardData {
  emails: Array<{
    messageId: string;
    threadId: string;
    /** Account this mail belongs to (for replying from the right mailbox). */
    integrationId?: string;
    fromName: string;
    fromEmail: string;
    subject: string;
    snippet: string;
    /** ISO 8601 timestamp. */
    date: string;
    isUnread: boolean;
    senderPhotoUrl?: string | null;
    /** Cached AI reply draft (seeds "Xavior write" instantly if present). */
    aiDraft?: string | null;
  }>;
}

/** A newly created note. `noteId` is the workspace OBJECT id (click-through). */
export interface NoteCardData {
  noteId: string;
  title: string;
  bodyPreview: string;
}

/** A newly created or referenced Drive doc. `docId` is the workspace OBJECT id (click-through). */
export interface DocCardData {
  docId: string;
  title: string;
  bodyPreview: string;
}

/** ONE card holding ALL matched contacts (rendered as a grid). Each `id` is
 *  the workspace OBJECT id so opening resolves the ContactCardPopover. */
export interface ContactCardData {
  contacts: Array<{
    id: string;
    name: string;
    email?: string;
    title?: string;
    company?: string;
    photoUrl?: string;
    /** One-line AI profile ("who this person is") — shown on the single-contact card. */
    bio?: string;
  }>;
}

/** One card per place. `placeId` is the workspace OBJECT id. */
export interface PlaceCardData {
  placeId: string;
  name: string;
  address?: string;
  category?: string;
  rating?: number;
}

/** ONE card holding ALL matched tasks (rendered as a checkbox list). Each `id`
 *  is the workspace OBJECT id so the inline checkbox writeback can PATCH it. */
export interface TaskCardData {
  tasks: Array<{
    id: string;
    title: string;
    completed: boolean;
    due?: string;
  }>;
}

/** End-of-turn "Open report" CTA for a report the agent built inline (#3793).
 *  Derived CLIENT-SIDE at stream finalize from the turn's report.init/
 *  report.section directives (helpers/report-result-card.ts) — the agent never
 *  emits this card itself. `moduleId` is the generated report module's id. */
export interface ReportCardData {
  moduleId: string;
  title: string;
}

export interface ResultCard {
  kind: ResultCardKind;
  /** For kind === 'email', `data` is an {@link EmailCardData}. */
  data: Record<string, unknown>;
}

/**
 * Split a persisted assistant message into its plain-markdown body and any
 * trailing result cards.
 *
 * Locates {@link RESULT_CARDS_MARKER}: everything before it (trimEnd) is
 * `cleanContent`; the substring after the marker is JSON.parsed as
 * `ResultCard[]`. On any error (no marker, bad JSON, non-array) it returns the
 * original content with no cards.
 */
export function splitResultCards(content: string): { cleanContent: string; cards: ResultCard[] } {
  const markerIndex = content.indexOf(RESULT_CARDS_MARKER);
  if (markerIndex === -1) return { cleanContent: content, cards: [] };

  const cleanContent = content.slice(0, markerIndex).trimEnd();
  const json = content.slice(markerIndex + RESULT_CARDS_MARKER.length);

  try {
    const parsed = JSON.parse(json) as unknown;
    if (!Array.isArray(parsed)) return { cleanContent: content, cards: [] };
    return { cleanContent, cards: parsed as ResultCard[] };
  } catch {
    return { cleanContent: content, cards: [] };
  }
}
